'use client'
import { useMemo, type FC, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import StyledComponentsRegistry from '@/utils/base/styledComponentsRegistry'
import { Susy } from '@/styles'
import { PRO, STAGING, IS_MMF_STORE, LANG } from '@/utils'
import { ModalsProvider, SessionProvider, ToastProvider, LocationProvider } from '@/context'
import { CartProvider } from '@/context/project/CartContext'
import { ProductProvider } from '@/context/project/ProductContext'
import type { TypeLang, TypeCmsCartLine, TypeCmsLayout, TypeCmsCustomer } from '@/schema'
import { E_BANNER_TYPE } from '@/schema'
import { Nav } from './layout/Nav'
import { SlimBanner } from './layout/Slimbanner'
import { Footer } from './layout/Footer'
import { InactivityManager, UrlNavigationManager } from '@/managers'

type Props = {
  layout: TypeCmsLayout
  children: ReactNode
  lang?: TypeLang
  initialCartLines?: TypeCmsCartLine[]
  initialCustomerToken?: string | null
  initialUser?: TypeCmsCustomer | null
}

const DynamicMenuAccount = dynamic(
  () =>
    import('@/components/project/modals/ModalMenuAccount').then(
      (mod) => mod.ModalMenuAccount
    ),
  { ssr: false }
)

const DynamicModalRegionStorePicker = dynamic(
  () =>
    import('@/components/project/modals/ModalRegionStorePicker').then(
      (mod) => mod.ModalRegionStorePicker
    ),
  { ssr: false }
)
const DynamicModalRegion = dynamic(
  () =>
    import('@/components/project/modals/ModalRegion').then(
      (mod) => mod.ModalRegion
    ),
  { ssr: false }
)
const DynamicModalCartType = dynamic(
  () =>
    import('@/components/project/modals/ModalCartType').then(
      (mod) => mod.ModalCartType
    ),
  { ssr: false }
)
const DynamicModalAuth = dynamic(
  () =>
    import('@/components/project/modals/ModalAuth').then(
      (mod) => mod.ModalAuth
    ),
  { ssr: false }
)
const DynamicModalNewsletter = dynamic(
  () =>
    import('@/components/project/modals/ModalNewsletter').then(
      (mod) => mod.ModalNewsletter
    ),
  { ssr: false }
)
const DynamicModalPromo = dynamic(
  () =>
    import('@/components/project/modals/ModalPromo').then(
      (mod) => mod.ModalPromo
    ),
  { ssr: false }
)
const DynamicModalSearch = dynamic(
  () =>
    import('@/components/project/modals/ModalSearch').then(
      (mod) => mod.ModalSearch
    ),
  { ssr: false }
)
const DynamicModalMenu = dynamic(
  () =>
    import('@/components/project/modals/ModalMenu').then(
      (mod) => mod.ModalMenu
    ),
  { ssr: false }
)
const DynamicModalCartControls = dynamic(
  () =>
    import('@/components/project/modals/ModalCartControls').then(
      (mod) => mod.ModalCartControls
    ),
  { ssr: false }
)
const DynamicModalStockCart = dynamic(
  () =>
    import('@/components/project/modals/ModalStockCart').then(
      (mod) => mod.ModalStockCart
    ),
  { ssr: false }
)
const DynamicProductDrawer = dynamic(
  () =>
    import('@/components/project/product/ProductDrawer').then(
      (mod) => mod.ProductDrawer
    ),
  { ssr: false }
)
const DynamicAsideWhatsapp = dynamic(
  () =>
    import('@/components/project/AsideWhatsapp').then(
      (mod) => mod.AsideWhatsapp
    ),
  { ssr: false }
)
const DynamicLoadingScreen = dynamic(
  () =>
    import('@/components/project/LoadingScreen').then(
      (mod) => mod.LoadingScreen
    ),
  { ssr: false }
)

const getValidModal = (modals, type) => {
  if (!modals || !Array.isArray(modals)) return []
  const cleanModal = []

  modals.map((modal) => {
    const { visibleFrom, visibleUntil, modalType } = modal

    const from = new Date(visibleFrom)
    const today = new Date()
    const to = new Date(visibleUntil)
    if (from <= today && to >= today && modalType === type) {
      cleanModal.push(modal)
    }
  })

  return cleanModal
}

const fallbackLayout:TypeCmsLayout = {
  categories: [],
  footers: [],
  modals: { edges: [] },
  banners: [],
}

const ClientProviders: FC<Props> = ({ layout, children, initialCartLines = [], initialCustomerToken = null, initialUser = null }) => {
  const { categories, modals, footers } = layout || fallbackLayout
  const modalNewsletter = useMemo(() => getValidModal(modals, 'newsletter'), [modals])
  const modalRegular = useMemo(() => getValidModal(modals, 'regular'), [modals])
  const slimBanners = useMemo(() => layout?.banners?.filter((el) => el.bannerType === E_BANNER_TYPE.SLIM || el.bannerType === E_BANNER_TYPE.SLIM?.toLowerCase()), [layout?.banners])
  const hasBanners = useMemo(() => slimBanners?.length > 0, [slimBanners])

  return (
    <StyledComponentsRegistry>
			<ToastProvider>
				<SessionProvider initialCustomerToken={initialCustomerToken} initialUser={initialUser}>
					<LocationProvider>
						<ProductProvider>
							<CartProvider initialCart={initialCartLines}>
								<ModalsProvider>
									{hasBanners && <SlimBanner banners={slimBanners} />}
									<Nav data={categories} />
									{children}
									<Footer pages={footers} firstCategory={categories?.[0]?.slug?.[LANG]}/>
									<DynamicModalRegion />
									<DynamicModalCartType />
									<DynamicProductDrawer />
									{IS_MMF_STORE && <DynamicAsideWhatsapp />}
									<DynamicMenuAccount />
									<DynamicModalRegionStorePicker />
									<DynamicModalCartControls />
									<DynamicModalStockCart />
									<DynamicModalAuth />
									<DynamicModalSearch
										hasBanners={hasBanners}
										categories={categories}
									/>
									<DynamicModalMenu data={layout} hasBanners={hasBanners} />
									{modalRegular.length > 0 && (
										<DynamicModalPromo data={modalRegular[0]} />
									)}
									{modalNewsletter.length > 0 && (
										<DynamicModalNewsletter data={modalNewsletter[0]} />
									)}
									<DynamicLoadingScreen />
									<UrlNavigationManager />
									<InactivityManager />
								</ModalsProvider>
							</CartProvider>
						</ProductProvider>
					</LocationProvider>
				</SessionProvider>
			</ToastProvider>
      {!STAGING && !PRO && <Susy />}
    </StyledComponentsRegistry>
  )
}

export default ClientProviders
